import { useEffect, useState, useContext, useCallback } from "react";
import CreateInstanceDialog from "../CreateInstanceDialog/CreateInstanceDialog";
import { createInstance } from "../../../API/Instance/Instance";
import InstancesGrid from "../InstancesGrid/InstancesGrid";
import RemoveDialog from "../../Shared/Remove/RemoveDialog";
import { deleteInstanceOnRuntime, disableInstanceOnRuntime, enableInstanceOnRuntime } from "../../Helpers/Runtime/runtime";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useMsal } from "@azure/msal-react";
import Loader from "../../Shared/Loader/loader";
import { sortInstancesByInstanceTypeAndUpdatedDate } from "./utils";
import { Header } from "../../Drawer/PageContainer";
import Upgrade from "../../Shared/Subscription/Upgrade";
import UpdateInstanceDialog from "../UpdateInstanceDialog/UpdateInstanceDialog";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import { getProject, updateProject, updateProjectInstance } from "../../../API/Project/project";
import { useTranslation } from "react-i18next";
import { Dialog } from "@progress/kendo-react-dialogs";
import { updateAllowEditAndPublishOnRuntime } from "../../../API/Runtime/RuntimeHelper";

/**
 * Instances component manages the display and operations on instances within a project.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.updateProjectData - Function to update project data.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <Instances updateProjectData={updateProjectDataFunction} />
 *
 * @description
 * This component fetches and displays instances related to a project. It provides functionalities to create, edit, delete, disable, and enable instances. It also handles various dialogs and loaders for these operations.
 *
 * @function
 * @name Instances
 *
 * @property {Function} fetchInstances - Fetches instances from the server.
 * @property {Function} toggleDialog - Toggles the visibility of a dialog.
 * @property {Function} isNameInUse - Checks if an instance name is already in use.
 * @property {Function} toggleLoader - Toggles the visibility of the loader.
 * @property {Function} handleCreate - Handles the creation of a new instance.
 * @property {Function} handleEditClick - Handles the click event for editing an instance.
 * @property {Function} handleUpdate - Handles the update of an instance.
 * @property {Function} handleDeleteClick - Handles the click event for deleting an instance.
 * @property {Function} deleteInstance - Deletes an instance.
 * @property {Function} handleDisableClick - Handles the click event for disabling an instance.
 * @property {Function} disableInstance - Disables an instance.
 * @property {Function} handleEnableClick - Handles the click event for enabling an instance.
 * @property {Function} handleCopy - Handles the copy event for an instance.
 */
function Instances({ updateProjectData }) {
    const { t } = useTranslation(["Instances/Instances"]);
    const msalContext = useMsal();
    const { subscription, organization } = useContext(GlobalContext).state;

    const organizationId = usePropsStoreValue("organizationId");
    const projectName = usePropsStoreValue("projectName");
    const project = usePropsStoreValue("project");

    const [instances, setInstances] = useState([]);
    const [showCreateInstanceDialog, setShowCreateInstanceDialog] = useState(false);
    const [showDeleteInstanceDialog, setShowDeleteInstanceDialog] = useState(false);
    const [idOfInstanceToDelete, setIdOfInstanceToDelete] = useState(null);
    const [showDisableInstanceDialog, setShowDisableInstanceDialog] = useState(false);
    const [idOfInstanceToDisable, setIdOfInstanceToDisable] = useState(null);
    const [showCopyToast, setShowCopyToast] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [indexOfInstanceToEdit, setIndexOfInstanceToEdit] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState([]);

    const fetchInstances = useCallback(async () => {
        try {
            const fetchedProject = await getProject(project._id);
            setInstances(sortInstancesByInstanceTypeAndUpdatedDate(fetchedProject.instances));
        } catch (error) {
            console.error("Failed to fetch environments", error);
        } finally {
            setShowLoader(false);
        }
    }, [project._id]);

    useEffect(() => {
        fetchInstances();
    }, [fetchInstances]);

    const toggleDialog = (setter) => () => setter(prev => !prev);

    const isNameInUse = (name) => instances.some(instance => instance.instanceName.trim().toLowerCase() === name.trim().toLowerCase());

    const toggleLoader = () => setShowLoader(prev => !prev);

    const handleCreate = async (instanceDetails) => {
        toggleLoader();
        const instanceResponse = await createInstance(subscription._id, project._id, instanceDetails);
        if (instanceResponse.limitReached) {
            toggleDialog(setShowUpgradeDialog)();
        } else {
            const newInstances = [...instances, instanceResponse];
            setInstances(sortInstancesByInstanceTypeAndUpdatedDate(newInstances));
            updateProjectData(newInstances);
        }
        toggleDialog(setShowCreateInstanceDialog)();
        toggleLoader();
    };

    const handleEditClick = (_id) => {
        const index = instances.findIndex(instance => instance._id === _id);
        setIndexOfInstanceToEdit(index);
        toggleDialog(setShowEditDialog)();
    };

    const handleUpdate = async (instanceDetails) => {
        toggleLoader();
        const allInstances = [...instances];
        const savingInstance = allInstances[indexOfInstanceToEdit];
        const response = await updateProjectInstance(project._id, savingInstance._id, instanceDetails);
        allInstances[indexOfInstanceToEdit] = response;
        setInstances(sortInstancesByInstanceTypeAndUpdatedDate(allInstances));
        updateProjectData(allInstances);
        toggleDialog(setShowEditDialog)();
        toggleLoader();
    };

    const handleDeleteClick = (instanceId) => {
        if (!validateAllowEditAndPublish(instanceId)) {
            toggleDialog(setShowWarningDialog)();
            setErrorMessage("Allow Edit and Publish is disabled for higher environment and lower envirooment are either disabled or not published. Please enable or publish lower environment to delete this environment.");
            return;
        }
        setIdOfInstanceToDelete(instanceId);
        toggleDialog(setShowDeleteInstanceDialog)();
    };

    const deleteInstance = async () => {
        toggleLoader();
        const response = await deleteInstanceOnRuntime(msalContext, organizationId, projectName, idOfInstanceToDelete);
        if (response.status === "Success") {
            const updatedInstances = instances.filter(instance => instance._id !== idOfInstanceToDelete);
            await updateProject(project._id, { instances: updatedInstances });
            setInstances(updatedInstances);
            updateProjectData(updatedInstances);
        }
        toggleDialog(setShowDeleteInstanceDialog)();
        toggleLoader();
    };

    const handleDisableClick = (instanceId) => {
        if (!validateAllowEditAndPublish(instanceId)) {
            toggleDialog(setShowWarningDialog)();
            setErrorMessage("Allow Edit and Publish is disabled for higher environment and lower envirooment are either disabled or not published. Please enable or publish lower environment to disable this environment.");
            return;
        }
        setIdOfInstanceToDisable(instanceId);
        toggleDialog(setShowDisableInstanceDialog)();
    };

    const validateAllowEditAndPublish = (instanceId) => {
        const instance = instances.find(i => i._id === instanceId);
        if (!instance) return false;

        const instanceWithAllowEditAndPublish = instances
            .filter(i => i._id !== instance._id && i.Published && i.allowEditAndPublish);

        return instanceWithAllowEditAndPublish.length > 0;
    };

    const disableInstance = async () => {
        toggleLoader();
        const response = await disableInstanceOnRuntime(msalContext, organizationId, projectName, idOfInstanceToDisable);
        if (response.status === "Success") {
            await updateProjectInstance(project._id, idOfInstanceToDisable, { isActive: false });
            const updatedInstances = instances.map(instance => instance._id === idOfInstanceToDisable ? { ...instance, isActive: false } : instance);
            setInstances(updatedInstances);
            updateProjectData(updatedInstances);
        }
        toggleDialog(setShowDisableInstanceDialog)();
        toggleLoader();
    };

    const handleEnableClick = async (instanceId) => {
        toggleLoader();
        const response = await enableInstanceOnRuntime(msalContext, organizationId, projectName, instanceId);
        if (response.status === "Success") {
            await updateProjectInstance(project._id, instanceId, { isActive: true });
            const updatedInstances = instances.map(instance => instance._id === instanceId ? { ...instance, isActive: true } : instance);
            setInstances(updatedInstances);
            updateProjectData(updatedInstances);
        }
        toggleLoader();
    };

    const handleAllowEditAndPublish = async (instanceId) => {
        toggleLoader();
        try {
            const instance = instances.find(i => i._id === instanceId);
            const updatedInstance = { ...instance, allowEditAndPublish: !instance.allowEditAndPublish };
            const response = await updateAllowEditAndPublishOnRuntime(instanceId, updatedInstance.allowEditAndPublish);
            if (response.status !== "Success") {
                console.error("Failed to update instance", response);
                throw new Error("Failed to update instance");
            }
            await updateProjectInstance(project._id, instanceId, { allowEditAndPublish: updatedInstance.allowEditAndPublish });
            const updatedInstances = instances.map(i => i._id === updatedInstance._id ? updatedInstance : i);
            setInstances(updatedInstances);
            updateProjectData(updatedInstances);
        } catch (error) {
            console.error("Failed to update instance", error);
            toggleDialog(setShowWarningDialog)();
            setErrorMessage(`Failed to update instance:` + error.message);
        } finally {
            toggleLoader();
        }
    };

    const handleCopy = () => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 2000);
    };

    return (
        <div className="h-100">
            <div className="chngBtn1">
                <Header
                    header={t("headers.environments")}
                    actionText={t("actions.new_environment")}
                    actionClicked={toggleDialog(setShowCreateInstanceDialog)}
                    actionDisabled={false}
                    showAction={true}
                />
            </div>
            {showLoader && <Loader />}
            {showCreateInstanceDialog && (
                <CreateInstanceDialog
                    isNameInUse={isNameInUse}
                    onCreate={handleCreate}
                    onCancel={toggleDialog(setShowCreateInstanceDialog)}
                />
            )}
            {showDeleteInstanceDialog && (
                <RemoveDialog
                    toggleDeleteDialog={toggleDialog(setShowDeleteInstanceDialog)}
                    onDelete={deleteInstance}
                    deleteButtonText={t("delete_dialog.buttons.delete")}
                    cancelButtonText={t("delete_dialog.buttons.cancel")}
                    Title={t("delete_dialog.enviroment_title")}
                    message={t("delete_dialog.enviroment_message", { instanceName: instances.find(instance => instance._id === idOfInstanceToDelete).instanceName })}
                    showLoader={showLoader}
                />
            )}
            {showWarningDialog &&
                <Dialog
                    title={"Warning Message"}
                    onClose={() => {
                        toggleDialog(setShowWarningDialog)();
                        setErrorMessage("")
                    }}
                    width={450}
                    height={200}
                >
                    <p className="mt-3 p-3">
                        {errorMessage}
                    </p>
                </Dialog>
            }
            {showDisableInstanceDialog && (
                <RemoveDialog
                    toggleDeleteDialog={toggleDialog(setShowDisableInstanceDialog)}
                    onDelete={disableInstance}
                    deleteButtonText={t("disable_dialog.buttons.disable")}
                    cancelButtonText={t("disable_dialog.buttons.cancel")}
                    Title={t("disable_dialog.enviroment_title")}
                    message={t("disable_dialog.enviroment_message", { instanceName: instances.find(instance => instance._id === idOfInstanceToDisable).instanceName })}
                    showLoader={showLoader}
                />
            )}
            {showUpgradeDialog && (
                <Upgrade
                    toggleDialog={toggleDialog(setShowUpgradeDialog)}
                    organization={{ id: organizationId }}
                />
            )}
            {showCopyToast && (
                <div className="position-fixed p-2" style={{ top: 88, left: "40%", transform: "translateX(-30%)", zIndex: 11 }}>
                    <div className="show toast text-success bg-white border-success align-items-center" role="alert" aria-live="assertive" aria-atomic="true">
                        <div className="d-flex">
                            <div className="toast-body font-size-1 text-center">
                                <i className="fa-regular fa-check-circle" />&nbsp;
                                {t("toasts.url_copied")}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showEditDialog && (
                <UpdateInstanceDialog
                    instance={instances[indexOfInstanceToEdit]}
                    isNameInUse={isNameInUse}
                    onCancel={toggleDialog(setShowEditDialog)}
                    onUpdate={handleUpdate}
                />
            )}
            <div className="container-fluid h-100 mt-3">
                <div className="row">
                    <div className="col-12">
                        <InstancesGrid
                            instances={instances}
                            onCopy={handleCopy}
                            onEdit={handleEditClick}
                            onDelete={handleDeleteClick}
                            onDisable={handleDisableClick}
                            onEnable={handleEnableClick}
                            onUpdate={handleUpdate}
                            onAllowEditAndPublish={handleAllowEditAndPublish}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Instances;
